import React from 'react';
import styled from 'styled-components';
import Button from '@/se/components/Button[DEPRECATED]';
import type { RetainedBeaconChoice, RetainedBeaconEligibility } from '@/hooks/useRetainedBeaconDischarge';
import { useScope } from '@/hooks/useScope';
import {
  formatNextProcedureLine,
  RETAINED_BEACON_DISCHARGE_COPY,
} from '@/components/entities/patient/views/RetainedBeaconDischargeChoice';
import { Modal, Subtitle, Title } from '../../../tablet/Modal';

interface Props {
  eligibility?: RetainedBeaconEligibility | null;
  onChoose: (choice: RetainedBeaconChoice) => void | Promise<void>;
  onCancel: () => void;
  working?: boolean;
}

/**
 * Full-screen retained-beacon confirmation for the PACU/Pre-Op/Post-Op kiosk.
 * It deliberately uses the existing tablet modal primitives instead of the
 * desktop Material UI dialog so its sizing and interaction match the kiosk.
 */
export function RetainedBeaconDischargeConfirmModal({
  eligibility,
  onChoose,
  onCancel,
  working = false,
}: Props) {
  const scope = useScope();
  const hospitalTimeZone = scope?.hospital?.timezone?.id ?? 'UTC';
  const nextProcedure = formatNextProcedureLine(eligibility?.scheduledStart, hospitalTimeZone);

  return (
    <Modal
      onClick={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="retained-beacon-kiosk-title"
    >
      <Title id="retained-beacon-kiosk-title">{RETAINED_BEACON_DISCHARGE_COPY.title}</Title>
      {nextProcedure && <Details>{nextProcedure}</Details>}
      <Prompt prominent>{RETAINED_BEACON_DISCHARGE_COPY.prompt}</Prompt>
      <Actions>
        <Button xl primary onClick={() => onChoose('retain')} disabled={working}>
          {RETAINED_BEACON_DISCHARGE_COPY.keepBracelet}
        </Button>
        <Button xl onClick={() => onChoose('standard')} disabled={working}>
          {RETAINED_BEACON_DISCHARGE_COPY.removeBracelet}
        </Button>
        <Button xl onClick={onCancel} disabled={working}>
          {RETAINED_BEACON_DISCHARGE_COPY.cancel}
        </Button>
      </Actions>
    </Modal>
  );
}

const Prompt = styled(Subtitle)`
  && {
    max-width: 48rem;
    margin: 0 0 2.5rem;
    font-size: 1.75rem;
  }
`;

const Details = styled.p`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  width: min(100%, 66rem);

  > button {
    width: 100%;
    min-height: 4.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
    white-space: normal;
    line-height: 1.25;
  }

  @media (max-width: 50rem) {
    grid-template-columns: 1fr;
    max-width: 28rem;
  }
`;
